// packages/ui/src/lib/compose.ts
//
// unified-ticket §7.5 — pure compose-box helpers dùng chung ở 3 app
// (merchant / admin / vendor). Không phụ thuộc React → test bằng vitest.

export type ComposeFileAttachment = {
  kind: 'file';
  fileId: string;
  filename: string;
  contentType: string;
  size: number;
};
export type ComposeExternalUrlAttachment = { kind: 'external-url'; url: string };
export type ComposeAttachment = ComposeFileAttachment | ComposeExternalUrlAttachment;

const IMAGE_URL_RE = /^https?:\/\/\S+\.(png|jpe?g|gif|webp|bmp|svg)(\?\S*)?$/i;

/** True when the string is an http(s) URL ending in a known image extension. */
export function isImageUrl(value: string): boolean {
  return typeof value === 'string' && IMAGE_URL_RE.test(value.trim());
}

export type ComposeKeyIntent = 'send' | 'newline' | 'none';

/** Enter = send; Alt(Option)/Shift + Enter = newline; else none. */
export function composeKeyIntent(e: { key: string; altKey: boolean; shiftKey: boolean }): ComposeKeyIntent {
  if (e.key !== 'Enter') return 'none';
  if (e.altKey || e.shiftKey) return 'newline';
  return 'send';
}

/** Human-readable file size. */
export function formatAttachmentSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const IMAGE_CONTENT_TYPE_RE = /^image\//i;

/**
 * True when an attachment should render inline as an image in the transcript:
 * a file with an image/* contentType, or an external-url pointing at an image.
 */
export function isImageAttachment(att: ComposeAttachment): boolean {
  if (att.kind === 'external-url') return isImageUrl(att.url);
  return IMAGE_CONTENT_TYPE_RE.test(att.contentType ?? '');
}

export interface TicketMessagePayload {
  content: string;
  attachments: ComposeAttachment[];
  relatedOrderIds: string[];
}

/** Assemble the POST /messages body: trim content, merge file + image-url attachments, dedup order ids. */
export function buildTicketMessagePayload(input: {
  content: string;
  fileAttachments?: ComposeFileAttachment[];
  imageUrls?: string[];
  orderIds?: string[];
}): TicketMessagePayload {
  // Dedup files by fileId and image-urls by url (paste can add the same twice).
  const seenFile = new Set<string>();
  const files: ComposeFileAttachment[] = [];
  for (const f of input.fileAttachments ?? []) {
    if (f && !seenFile.has(f.fileId)) {
      seenFile.add(f.fileId);
      files.push(f);
    }
  }
  const seenUrl = new Set<string>();
  const urls: ComposeExternalUrlAttachment[] = [];
  for (const url of input.imageUrls ?? []) {
    if (url && !seenUrl.has(url)) {
      seenUrl.add(url);
      urls.push({ kind: 'external-url', url });
    }
  }
  const attachments: ComposeAttachment[] = [...files, ...urls];
  const seen = new Set<string>();
  const relatedOrderIds: string[] = [];
  for (const id of input.orderIds ?? []) {
    if (id && !seen.has(id)) {
      seen.add(id);
      relatedOrderIds.push(id);
    }
  }
  return { content: input.content.trim(), attachments, relatedOrderIds };
}
